import { buildCampaignPath, buildTemplatePath, buildViewPath, buildNewTabPath, buildDocsPath, buildDashboardsPath, buildCampaignsPath } from '../../utils/routing';

/**
 * Generate a unique tab ID
 */
export const generateTabId = () => `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Build URL path for a tab based on its type and content
 */
export const buildTabPath = (type, targetId, viewMode, customViewId = null) => {
  switch (type) {
    case 'campaign':
      return buildCampaignPath(targetId, viewMode, { customViewId });
    case 'template':
      return buildTemplatePath(targetId, viewMode);
    case 'view':
      return buildViewPath(targetId, viewMode);
    case 'blank':
      return buildNewTabPath();
    case 'doc':
    case 'docs':
      return buildDocsPath();
    case 'dashboards':
      return buildDashboardsPath();
    case 'campaigns':
      return buildCampaignsPath();
    default:
      return '/';
  }
};

/**
 * Campaigns home chip — the place you stand when nothing particular is open.
 * Used when the last tab closes so the main view is never tab-less.
 */
export function createCampaignsHomeTab() {
  const label = 'Campaigns';
  const initialHistoryEntry = {
    type: 'campaigns',
    targetId: null,
    viewMode: null,
    customViewId: null,
    label,
    icon: null,
  };
  return {
    id: generateTabId(),
    type: 'campaigns',
    targetId: null,
    label,
    icon: null,
    viewMode: null,
    customViewId: null,
    path: buildTabPath('campaigns', null, null),
    history: [initialHistoryEntry],
    historyIndex: 0,
  };
}

/**
 * Tab shape documentation:
 * {
 *   id: string,           // Unique tab identifier
 *   type: 'campaign' | 'template' | 'view' | 'blank' | 'doc' | 'docs' | 'dashboards' | 'campaigns',
 *   targetId: string|null, // ID of the campaign/template/view/doc (null for blank / area homes)
 *   label: string,        // Display name (cached for quick rendering)
 *   icon: string|null,    // Emoji/icon (cached; null for blank)
 *   viewMode: string,     // Current view mode within this tab (flow, kanban, etc.)
 *   customViewId: string, // ID of the specific custom view (for deep linking)
 *   path: string,         // URL path for this tab (derived from type/targetId/viewMode/customViewId)
 *   
 *   // Persisted overlay state (survives app restart):
 *   openMissionId: string|null,   // Mission Full View open in this tab
 *   missionDocStack: string[],    // Doc sheet stack opened from that mission
 *   missionDocDirect: boolean,    // Doc opened from a card, not from Mission Full View
 *   missionDocScrollById: { [docId]: number }, // Doc sheet scrollTop per doc
 *   docStack: string[],           // Library doc trail for this tab
 *   docScrollById: { [docId]: number }, // Library doc scrollTop per doc
 *
 *   // SESSION-ONLY fields (not persisted to backend, reset on refresh):
 *   history: Array<HistoryEntry>, // Navigation history for this tab
 *   historyIndex: number,         // Current position in history (0 = oldest)
 * }
 *
 * Related session memory (module store, keyed by tab):
 * - Flow pan/zoom: FlowchartView/hooks/useViewportInitialization.js
 *
 * HistoryEntry shape:
 * {
 *   type: string,
 *   targetId: string,
 *   viewMode: string,
 *   customViewId: string,
 *   label: string,
 *   icon: string,
 * }
 */
